import { useEffect, useState } from 'react';
import { Copy, Check, Server, Info, RefreshCw } from 'lucide-react';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'sonner';
import { copyTextToClipboard } from '@/lib/clipboard';
import { mcpCatalogAPI, type McpCatalogServer } from '@/services/mcpCatalogApi';

export function MyMCPConfigPage() {
  const { user, accessToken } = useAuthStore();
  const [copied, setCopied] = useState<string | null>(null);
  const [servers, setServers] = useState<McpCatalogServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiKey = user?.oneapi_token || '';
  const apiKeyMasked = apiKey ? `${apiKey.slice(0, 6)}••••••` : '<你的 API Key>';

  const loadServers = async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await mcpCatalogAPI.listServers(accessToken);
      setServers(resp.data?.servers || []);
    } catch (e: any) {
      setError(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleCopy = async (text: string, key: string) => {
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopied(key);
      toast.success('已复制到剪贴板');
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.error('复制失败，请手动复制');
    }
  };

  if (!user) {
    return (
      <LayoutWithSidebar title="MCP 服务器">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>请先登录查看 MCP 服务器</AlertDescription>
        </Alert>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="MCP 服务器">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">MCP 服务器</h1>
        <p className="text-gray-600 dark:text-gray-400">
          以下是当前可用的 MCP 服务器。在 App 中从列表选择并添加你需要的服务器；每个服务器提供一组工具。
        </p>
      </div>

      {/* 鉴权信息 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            连接鉴权
          </CardTitle>
          <CardDescription>手动配置 MCP 客户端时需要的鉴权信息</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">API Key（必填请求头）</label>
            <p className="mt-1 text-base font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded break-all">
              Authorization: Bearer {apiKeyMasked}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              连接任意 MCP 服务器都必须携带此请求头。
              {apiKey ? '' : ' 你的账号还没有主令牌，请到“个人中心/额度”页面刷新令牌。'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 可用服务器列表 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>可用的 MCP 服务器</CardTitle>
              <CardDescription>数据来自 MCPHub，实时反映当前配置的服务器</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={loadServers} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert className="bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800">
              <Info className="h-4 w-4 text-red-600 dark:text-red-500" />
              <AlertDescription className="text-red-800 dark:text-red-200">
                {error}
                <Button size="sm" variant="link" className="ml-2 h-auto p-0" onClick={loadServers}>
                  重试
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {!error && !loading && servers.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">暂无可用的 MCP 服务器</p>
          )}
          {servers.map((s) => (
            <div key={s.name} className="border rounded-lg p-4 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">{s.name}</span>
                <Badge variant={s.status === 'connected' ? 'default' : 'secondary'}>
                  {s.status === 'connected' ? '在线' : '离线'}
                </Badge>
                <span className="text-xs text-gray-500">{s.toolCount} 个工具</span>
              </div>
              {s.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{s.description}</p>
              )}
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto text-xs">
                  <code>{s.endpoint}</code>
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-1 right-1 h-7"
                  onClick={() => handleCopy(s.endpoint, s.name)}
                >
                  {copied === s.name ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          ))}

          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-500" />
            <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>如何使用：</strong>在 App 的“MCP 服务器”页面从列表中选择并添加你需要的服务器（App 会自动带上你的 API Key）。
              高级用户也可在支持 Streamable HTTP 的 MCP 客户端里手动填入上面的地址，并加上
              <code className="mx-1">Authorization: Bearer &lt;API Key&gt;</code>请求头。
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </LayoutWithSidebar>
  );
}
